import * as React from "react";

import { Block } from "baseui/block";
import { Map } from "../Map";
import { MapMarker } from "../Map/MapMarker";
import { Select } from "../Select";
import { deref } from "..//utils/primitives";
import { useGoogleAddressInput } from "./useGoogleAddressInput";
import { useShowMap } from "./useShowMap";
import { useBlueZebraAddressInput } from "./useBlueZebraAddressInput";
import { useGate } from "statsig-react";
import { convertCamelKeysToPascalKeys } from "../utils";

// Define the props interface
interface AddressInputProps {
  id?: string;
  error?: string; // Assuming error is a string, adjust the type if necessary
  onError?: (error: string) => void; // Adjust based on actual usage
  isValidating?: boolean;
  onValidating?: (isValidating: boolean) => void; // Adjust based on actual usage
  withMap?: boolean;
  getClimateData?: () => void; // Adjust based on actual function signature
  integrations: Array<"bz">;
  value?: unknown; // Specify a more accurate type based on what 'value' is supposed to be
  // You can add specific types for the rest of the props if they are known
  [restProps: string]: unknown;
}

function AddressInput(props: AddressInputProps) {
  const { value: validateAddressInBzFlag } = useGate("k-44_validate_address_in_bz");

  const { id = "address", value, integrations, ...restProps } = props;
  const showMap = useShowMap(props);
  const latLng = getLatLng(value);

  let labelKey;
  let addressInputProps;
  if (validateAddressInBzFlag) {
    labelKey = "AddressText";
    // BZ address search returns PascalCase keys, but the input value uses camelCase keys.
    // Convert the input value keys to PascalCase to match the BZ format.
    const formattedValue = convertCamelKeysToPascalKeys(value);
    const updatedProps = { ...props, value: formattedValue };
    addressInputProps = useBlueZebraAddressInput(updatedProps, integrations);
  } else {
    labelKey = "description";
    addressInputProps = useGoogleAddressInput(props, integrations);
  }

  return (
    <Block
      overrides={{
        Block: {
          style: {
            alignItems: "normal",
          },
        },
      }}
    >
      {" "}
      <Select
        id={id}
        placeholder="Enter address"
        {...restProps}
        {...addressInputProps}
        value={value}
        full
        searchable
        labelKey={labelKey}
        // Crappy fix for exact string matching on addresses in baseweb
        filterOptions={(options) => options}
      />
      {showMap && (
        <Block marginTop="scale200">
          <Map center={latLng} options={{ disableDefaultUI: true }}>
            {latLng && <MapMarker {...latLng} />}
          </Map>
        </Block>
      )}
    </Block>
  );
}

type LatLng = {
  lat: number;
  lng: number;
};

const getLatLng = (value): LatLng => {
  const location = deref(value, "geometry.location");
  // Should useAddressInput return a specific, final address shape instead?
  if (!location) return;
  const { lat, lng } = location;
  return {
    lat: typeof lat === "function" ? lat() : lat,
    lng: typeof lng === "function" ? lng() : lng,
  };
};

export { AddressInput };
